import React from "react";
import { amountLabel, changeLabel, count, deltaTone, minutes, minutesPerKm, pct, rupeesWhole } from "../format";

function Sparkline({ values, color = "#fb310d" }) {
  if (!values || values.length < 2) {
    return <div className="ins-sparkline-wrap" aria-hidden="true" />;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((val, idx) => {
    const x = (idx / (values.length - 1)) * 100;
    const y = 20 - ((val - min) / range) * 16;
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  });

  const linePath = `M ${points.join(" L ")}`;
  const areaPath = `M 0 24 L ${points.join(" L ")} L 100 24 Z`;

  return (
    <div className="ins-sparkline-wrap">
      <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 100 24" style={{ width: "100%", height: "100%" }}>
        <path d={areaPath} fill={color} fillOpacity="0.1" />
        <path d={linePath} stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
    </div>
  );
}

export default function KpiTiles({ tiles, daily, amount = "food" }) {
  const rev = tiles && tiles.revenue;
  const ord = tiles && tiles.orders;
  const aov = tiles && tiles.avg_order_value;
  const wait = tiles && tiles.avg_wait_minutes;
  const perKm = tiles && tiles.avg_minutes_per_km;
  const earn = tiles && tiles.store_earnings;
  const disc = tiles && tiles.discount_given;
  const cust = tiles && tiles.new_customers;
  const rep = tiles && tiles.repeat_rate;

  const revSeries = (daily || []).map((d) => d.revenue || 0);
  const ordSeries = (daily || []).map((d) => d.orders || 0);

  const getBadge = (tile, betterWhen = "up") => {
    if (!tile || tile.change_pct === null || tile.change_pct === undefined) return null;
    const chg = changeLabel(tile);
    const tone = deltaTone(betterWhen, tile.change_pct);
    const badgeClass = tone === "good" ? "good" : tone === "bad" ? "bad" : "neutral";
    return <span className={`ins-kpi-badge ${badgeClass}`}>{chg.text.replace(" vs previous", "")}</span>;
  };

  const revenueTitle = amount === "food" ? "Revenue" : amountLabel(amount);
  const revenueHint = amount === "food" ? "Food amount, after offers and coupons" : "Realised orders in this range";
  const sharesApply = amount === "food" || amount === "total";
  const storeShare = sharesApply && rev && rev.value && earn && earn.value != null ? (earn.value / rev.value) * 100 : null;
  const storeShareText = storeShare == null ? "Paid out to stores" : `${storeShare.toFixed(1)}% of revenue goes to stores`;

  const discShare = sharesApply && rev && rev.value && disc && disc.value != null ? (disc.value / rev.value) * 100 : null;
  const discRate = discShare == null ? "--" : `${discShare.toFixed(1)}%`;

  const speedStatus =
    wait && wait.value != null
      ? wait.value <= 30
        ? "Fast speed zone"
        : wait.value <= 45
        ? "Standard speed"
        : "High delay zone"
      : "Speed tracking";

  return (
    <section className="ins-section">
      <div className="ins-kpi-grid">
        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">{revenueTitle}</span>
              {getBadge(rev, "up")}
            </div>
            <div className="ins-kpi-num">{rupeesWhole(rev ? rev.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>{rev && rev.prev != null ? `vs ${rupeesWhole(rev.prev)} prev` : revenueHint}</span>
            </div>
          </div>
          <Sparkline values={revSeries} color="#fb310d" />
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Orders</span>
              {getBadge(ord, "up")}
            </div>
            <div className="ins-kpi-num">{count(ord ? ord.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>{ord && ord.prev != null ? `vs ${count(ord.prev)} prev` : "Placed in this range"}</span>
            </div>
          </div>
          <Sparkline values={ordSeries} color="#4648d4" />
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Avg Order Value</span>
              {getBadge(aov, "up")}
            </div>
            <div className="ins-kpi-num">{rupeesWhole(aov ? aov.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>{aov && aov.prev != null ? `vs ${rupeesWhole(aov.prev)} prev` : `${amountLabel(amount)} per realised order`}</span>
            </div>
          </div>
          <div className="ins-sparkline-wrap" aria-hidden="true" />
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Avg Delivery Time</span>
              {getBadge(wait, "down")}
            </div>
            <div className="ins-kpi-num">{minutes(wait ? wait.value : null)}</div>
            <div className="ins-kpi-sub" style={{ color: wait && wait.value <= 30 ? "#059669" : "#64748b" }}>
              <span>{speedStatus}</span>
            </div>
          </div>
          <div className="ins-sparkline-wrap" aria-hidden="true" />
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Avg Time / km</span>
              {getBadge(perKm, "down")}
            </div>
            <div className="ins-kpi-num">{minutesPerKm(perKm ? perKm.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>Rider leg over straight-line km</span>
            </div>
          </div>
          <div className="ins-sparkline-wrap" aria-hidden="true" />
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Store Earnings</span>
              <span className="ins-kpi-badge neutral">Store share</span>
            </div>
            <div className="ins-kpi-num">{rupeesWhole(earn ? earn.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>{storeShareText}</span>
            </div>
          </div>
          <div className="ins-kpi-meter">
            <div className="ins-kpi-meter-bar" style={{ width: `${storeShare == null ? 0 : Math.min(100, Math.round(storeShare))}%`, backgroundColor: "#6366f1" }} />
          </div>
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">Discounts Given</span>
              <span className="ins-kpi-badge amber">{discRate}</span>
            </div>
            <div className="ins-kpi-num">{rupeesWhole(disc ? disc.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>{disc && disc.prev != null ? `vs ${rupeesWhole(disc.prev)} prev` : "of revenue, as item offers and coupons"}</span>
            </div>
          </div>
          <div className="ins-kpi-meter">
            <div className="ins-kpi-meter-bar" style={{ width: `${discShare == null ? 0 : Math.min(100, Math.round(discShare))}%`, backgroundColor: "#f59e0b" }} />
          </div>
        </div>

        <div className="ins-kpi-tile">
          <div>
            <div className="ins-kpi-top">
              <span className="ins-kpi-title">New Customers</span>
              {getBadge(cust, "up")}
            </div>
            <div className="ins-kpi-num">{count(cust ? cust.value : null)}</div>
            <div className="ins-kpi-sub">
              <span>Repeat rate: <strong>{pct(rep ? rep.value : null)}</strong></span>
            </div>
          </div>
          <div className="ins-kpi-meter">
            <div
              className="ins-kpi-meter-bar"
              style={{
                width: `${rep && rep.value ? Math.min(100, Math.round(rep.value)) : 0}%`,
                backgroundColor: "#fb310d",
              }}
            />
          </div>
        </div>
      </div>

      {tiles && (tiles.delivered || tiles.cancellation_rate || tiles.rejection_rate) && (
        <div className="ins-ops-strip">
          <span style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", letterSpacing: "0.04em" }}>
            Operational Health:
          </span>
          {tiles.delivered && (
            <div className="ins-ops-item">
              <span className="ins-pulse-dot" style={{ width: "6px", height: "6px" }} />
              <span style={{ color: "#475569" }}>Delivered:</span>
              <strong style={{ color: "#0f172a" }}>{count(tiles.delivered.value)} orders</strong>
            </div>
          )}
          {tiles.cancellation_rate && (
            <div className="ins-ops-item" style={{ marginLeft: "0.5rem" }}>
              <span style={{ color: "#475569" }}>Customer Cancellations:</span>
              <span className={`ins-pill-badge ${tiles.cancellation_rate.value <= 5 ? "green" : "rose"}`}>
                {pct(tiles.cancellation_rate.value)}
              </span>
            </div>
          )}
          {tiles.rejection_rate && (
            <div className="ins-ops-item" style={{ marginLeft: "0.5rem" }}>
              <span style={{ color: "#475569" }}>Store Rejections:</span>
              <span className={`ins-pill-badge ${tiles.rejection_rate.value <= 3 ? "green" : "amber"}`}>
                {pct(tiles.rejection_rate.value)}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
